import { Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import {
  PagedResult, StreamStatus, UploadDirListing, UploadFileEntry, UploadFileInfo,
  UploadFilePreview, UploadFolderInfo,
} from '../common/api.types';

// Re-exported so existing `from '.../app.service'` imports keep resolving.
export type {
  PagedResult, StreamStatus, UploadDirListing, UploadFileEntry, UploadFileInfo,
  UploadFilePreview, UploadFolderInfo,
};

export interface UploadResult {
  ok: boolean;
  savedName: string;
}

export interface ProfileTokenUsage {
  total: {
    total_calls: number;
    total_input_tokens: number;
    total_output_tokens: number;
    avg_input_tokens: number;
    avg_output_tokens: number;
  };
}

export interface Profile {
  dataset_name: string;
  dataset_type: string;
  n_rows?: number;
  n_columns?: number;
  token_usage?: ProfileTokenUsage;
}

export interface TransformStep {
  op: string;
  input?: string;
  output?: string;
  prefix?: string;
  suffix?: string;
  params?: Record<string, any>;
  [key: string]: any;
}

/** What the Execution preview is asked for. Every narrowing is the server's work: the
 *  preview is paged there, so a filter applied in the browser could only ever reach the
 *  rows already fetched and would report counts for a slice of the data. */
/** One source's own profile, in the shape the drawer already renders for the primary. */
export interface SourceProfile {
  name: string;
  label?: string;
  type: string;
  unique_count: number;
  null_pct: number;
  values?: TargetValueEntry[];
  example_mappings?: ExampleMapping[];
}

export interface ExecutionPreviewQuery {
  page: number;
  page_size: number;
  col_limit?: number;
  /** Keep only the columns produced by this decision. `not_mapped` returns an empty
   *  harmonized side: those columns produced nothing, and pairing them with whatever sat
   *  at the same index would invent a mapping that was never made. */
  decision?: string | null;
  /** Free text over COLUMN NAMES, matched on either side. A column is kept when it
   *  matches, and so is its counterpart on the other side — the pair only means anything
   *  read together. Cell values are the column filters' job, where each value comes with
   *  its count and the column it belongs to. */
  search?: string;
  /** Which side `search` matches. `harmonized` keeps the harmonized columns whose name
   *  matches AND the unharmonized ones that build them; `unharmonized` does the mirror.
   *  Omitted is bidirectional — either side matching keeps the pair — and is the default,
   *  because a reviewer looking for a name rarely knows which side spells it that way. */
  search_in?: 'unharmonized' | 'harmonized';
  /** Value picks per column, keyed `raw:<column>` or `harmonized:<column>`. */
  column_filters?: Record<string, string[]>;
  /** Cap on each column's `filter_values`. The server has its own default; this only
   *  overrides it. The complete list for one column comes from `getColumnValues`. */
  filter_values_limit?: number;
}

export interface ColumnValuesQuery {
  side: 'unharmonized' | 'harmonized';
  column: string;
  /** Same shape as the preview's, and the same rule: this column's own filter is lifted so
   *  it can still be widened. */
  column_filters?: Record<string, string[]>;
}

export interface ColumnValuesData {
  side: string;
  column: string;
  values: { value: string; count: number }[];
}

export interface ExecutionPreviewData {
  raw: CsvPreviewData;
  harmonized: CsvPreviewData;
  total: number;
  page: number;
  page_size: number;
}

export interface AuditVariable {
  id: string;
  decision: string;
  mapping_from: string | string[];
  mapping_to: string;
  decision_confidence?: number;
  target_confidence?: number;
  transform_confidence?: number;
  reasoning?: string;
  transform: { steps: TransformStep[] };
  metadata?: { review_reasons?: string[] };
  reviewed?: boolean;
}

export interface TargetValueEntry { value: string; label: string; }
export interface ExampleMapping { from: string; to: string; }

export interface TargetHelp {
  description: string;
  what_it_means: string;
  values: TargetValueEntry[];
  relevant_info: string[];
  example_mappings: ExampleMapping[];
  // "Details" section (mirrors the source stats) so Source and Target render identically
  type?: string;
  unique_count?: number;
  null_pct?: number;
}

export interface TargetAlternative {
  id: string;
  label: string;
  confidence: number;
  system_selected: boolean;
  help: TargetHelp;
}

export interface TransformPreviewRow { original: string; new_value: string; }

export interface TransformationOption {
  id?: string;   // stable id from the backend — present on every transformation (used to edit-in-place / reference). Optional as a safety net in case the backend omits it.
  rank: number;
  title: string;
  description?: string;
  confidence: number;
  system_selected: boolean;
  ai_suggested?: boolean;
  notes?: string;
  preview: TransformPreviewRow[];
  code: Record<string, unknown> | unknown[];
}

export interface TransformChatMessage {
  role: 'user' | 'assistant';
  content: string;
  transformation?: TransformationOption;
  error?: boolean;
  initial?: boolean;
  isHtml?: boolean;
}

export interface TransformSuggestion {
  message: string;
  transformation?: Partial<TransformationOption>;
}

export interface ReviewVariableData {
  source_info: {
    id: string;
    decision: string;
    decision_system_selected: string | null;
    mapping_from: string | string[];
    mapping_from_system_selected?: string[] | null;
    mapping_to: string;
    label?: string;
    type: string;
    unique_count: number;
    null_pct: number;
    values?: TargetValueEntry[];   // source value list, shown as a Values table like the target
    example_mappings?: ExampleMapping[];   // so Source shows the same "Example mapping" section as Target
    /** One entry per name in `mapping_from`. For an n:1 mapping the fields above describe
     *  the PRIMARY source only, which is why the drawer read as one variable's profile under
     *  a title naming several. A 1:1 mapping is a single-element array. */
    sources?: SourceProfile[];
    reasoning?: string;                    // so Source shows the same "Reasoning" section as Target
    decision_confidence: number;
    decision_confidences: Record<string, number>;
    target_confidence: number | null;
    transform_confidence: number | null;
    selected_transform_rank?: number | null;
    reviewed?: boolean;   // whether this variable has already been reviewed (drives the "Already reviewed" badge)
  };
  target_alternatives: TargetAlternative[];
  transformation_options: TransformationOption[];
}

export interface TargetReloadData {
  target_alternatives: TargetAlternative[];
  transformation_options: TransformationOption[];
  // The plan's original sources, frozen before review. The backend only fills this for
  // calculated_variable (adapters._norm_variable), so mapped/partially_mapped get null and
  // show no "AHA's pick" badge — a backend change is needed for parity.
  mapping_from_system_selected?: string[] | null;
}

export interface TransformReloadData {
  transformation_options: TransformationOption[];
}

export interface PlanJson {
  plan_name: string;
  variables: AuditVariable[];
}

export interface HumanReviewQueue {
  ids: string[];
  total: number;
}

/** One row of the execution breakdown: how many variables of a decision the plan held, and
 *  how many of those the run actually produced. `executed` is null for `not_mapped`, which
 *  never runs — it has no target column to write into, so 0 would read as a failure. */
export interface ExecutionDecisionStat {
  decision: 'mapped' | 'partially_mapped' | 'calculated_variable' | 'matched' | 'not_mapped';
  in_plan: number;
  executed: number | null;
  /** This decision's share of the plan, and how much of it the run produced. Both are the
   *  server's: the denominators differ — the share is measured against the whole plan, the
   *  rate against this decision alone — and one place should decide that. Sent at full
   *  precision; rounding is the client's, so the stacked bar can still add up to 100. */
  share_pct: number;
  /** Null wherever `executed` is: a decision that never runs has no rate to report. */
  success_pct: number | null;
}

export interface ExecutionSummary {
  total_in_plan: number;
  /** Everything the run was expected to produce — the plan minus the decisions that never
   *  run. It is the denominator of the success rate; using total_in_plan instead would
   *  charge the run for variables it was never asked to write. */
  total_executable: number;
  total_executed: number;
  by_decision: ExecutionDecisionStat[];
}

/** One variable of the plan that ran and produced no column. */
export interface ExecutionFailedVariable {
  id: string;
  decision: string;
  mapping_from: string[];
  mapping_to: string;
  /** One line, already written to be read. Always present — when the run cannot attribute
   *  the failure to anything, it says that in words. */
  reason: string;
}

export interface ExecutionFailures {
  variables: ExecutionFailedVariable[];
}

export interface ImageFolderList {
  directories: string[];
}

export interface ImageSummaryData {
  ok: boolean;
  counts: {
    identified: number;
    skull_stripping: number;
    reorientation: number;
    registration: number;
  };
}

export interface ImageGifList {
  ok: boolean;
  gifs: string[];
  total: number;
  page: number;
  page_size: number;
}

export type ProfileVariableRow = {
  variable_name: string;
  data_type: string;
  description: string;
  missing_pct?: number | null;
  confidence_data_type?: number | null;
  confidence_description?: number | null;
  confidence_values?: number | null;
  confidence_overall?: number | null;
  values?: any;
};

export interface ProfileVariableEditDefaults {
  // The edit page's sole source of truth for what should be pre-selected when
  // editing this variable — it does not fall back to the main profile list, including
  // the variable's own name (the route params only pick which JSON file to fetch).
  variable_name: string;
  // Each field has a "current" value (user-editable, what actually gets saved) and a
  // "_system_selected" value (the AI's original single prediction — fixed, never
  // changes as the user edits). Both start out identical; only the current value moves
  // as the user edits. This mirrors decision vs decision_system_selected in Review.
  data_type: string;
  data_type_system_selected: string;
  description: string;
  description_system_selected: string;
  values: any;
  values_system_selected: any;
}

export interface ProfileVariableEditPatch {
  description: string;
  data_type: string;
  values: any;
}

export interface CsvPreviewData {
  columns: string[];
  rows: Record<string, string>[];
  /** The values each of this side's columns can be filtered by, with how often each
   *  occurs — what the column funnels offer. Keyed by column name; the side is implied by
   *  which preview carries it. Present only where the caller asked for filtering. */
  filter_values?: Record<string, { value: string; count: number }[]>;
  /** Per column, whether its `filter_values` list was cut off by the cap. `true` means the
   *  column has more distinct values than came back, so its funnel has to ask for the rest
   *  rather than present what it has as the whole set. */
  filter_values_truncated?: Record<string, boolean>;
}

export interface ValidationRow {
  variable: string;
  decision_id: string | null;
  mapping_from: string[];
  harmonized: boolean | null;
  concept: string;
  datatype: string;
  coverage: string;
  missing: string;
  stats: string;
}

export interface SplitVariableResult {
  created_variable_ids: string[];
}

export interface PipelineStats {
  dataset_name: string;
  modules: { label: string; minutes: number }[];
}

export interface PlanQueryOpts {
  /** Active filter values sent as comma-separated `filter=` param.
   *  Possible values: matched | mapped | partially_mapped | calculated_variable | not_mapped
   *                   intervention_needed | intervention_not_needed */
  filters?: string[];
  /** When true, sends replan_filter=true — restricts to replan-failed variables */
  replanFilter?: boolean;
  /** Confidence threshold (0–1); required when filters includes intervention_needed or intervention_not_needed */
  threshold?: number;
  /** Server-side sort (confidence_desc | confidence_asc | reviewed_first | reviewed_last) */
  sortBy?: string;
}

export interface ProfileQueryOpts {
  /** Server-side sort: missing_desc | missing_asc | name_asc | name_desc */
  sortBy?: string;
  /** Active filter values sent as comma-separated `filter=` param.
   *  Possible values: data types — int | float | date | binary | categorical_nominal | categorical_ordinal | text */
  filters?: string[];
  /** Ordered `variable_name`s to surface first (`priority_ids`) — used by the review Sources picker. */
  priorityIds?: string[];
}

export interface ValidationSummary {
  total: number;
  harmonized: number;
  not_harmonized: number;
  not_mapped: number;
  harmonization_rate: number;
  dataset_name: string;
}

export interface SessionFiles {
  unharmonized_dataset: UploadFileInfo;
  standard_dataset: UploadFileInfo;
  unharmonized_codebook: UploadFileInfo;
  standard_codebook: UploadFileInfo;
}

export interface Session {
  session_id: string;
  name: string;
  /** `canceled` is a user-stopped run. The backend stores it as failed with a marker and
   *  surfaces it under its own name, so the label stays accurate; it resumes exactly like a
   *  failed one, since `current_step` still carries the `_failed` suffix. */
  status: 'running' | 'finished' | 'failed' | 'canceled';
  current_step: string;
  /** Who created the run. Empty string when the backend has no user attached — shown only
   *  when it carries something, since "Created by: " with nothing after it says less than
   *  no line at all. */
  user_email?: string;
  created_at: string;
  updated_at: string;
  filter_active?: boolean;
  files?: SessionFiles;
}

@Injectable()
export abstract class AppService {
  // Stored by the service after a successful Start Profiling / Start Preprocessing.
  // Components never touch this — all real-service methods read it internally.
  sessionId = '';
  sessionName = signal('');
  pendingSessionFiles = signal<SessionFiles | null>(null);

  setSession(id: string): void { this.sessionId = id; }

  /** The run this page belongs to, resolved the same way the service resolves it for a
   *  request — the field first, then the stored one, since a reload leaves `sessionId`
   *  empty until the first call restores it. Empty string when there is no run. */
  currentSessionId(): string {
    if (!this.sessionId && typeof localStorage !== 'undefined') {
      this.sessionId = localStorage.getItem('aha_session_id') ?? '';
    }
    return this.sessionId;
  }
  abstract setRunName(name: string): void;

  /** `serverPath` is the file's path relative to the files root — what the backend
   *  registers it under. It differs from `file.name` (the basename) for anything in a
   *  subfolder, so the picker passes it explicitly; a locally chosen file has no server
   *  path and falls back to its name. */
  abstract uploadFile(file: File, card: string, slot: number, serverPath?: string): Promise<UploadResult>;
  abstract saveSteps(decisionId: string, steps: unknown[]): void;
  abstract getHumanReviewQueue(threshold: number | null, unreviewed?: boolean): Observable<HumanReviewQueue>;
  abstract checkAllNotMapped(): Observable<{ all_not_mapped: boolean }>;
  abstract getProfiles(): Observable<Record<string, Profile>>;
  // ── File picker (lazy) ────────────────────────────────────
  // The tree is loaded one folder at a time, and a file's full preview is a
  // second call made only for the file the user actually opens. Both mirror the
  // backend, which never walks the tree or opens a file to build a listing.

  /** Immediate children of one folder. `path` is relative to the files root; `''` = root. */
  abstract browseUploadDir(path: string): Observable<UploadDirListing>;
  /** Full preview (CSV headers/rows) for a single file, by its `path`. */
  abstract getUploadFilePreview(path: string): Observable<UploadFilePreview>;
  /** Raw-content URL, for the PDF iframe `src`. Takes a file's `path`. */
  abstract getUploadFilePreviewUrl(path: string): string;
  abstract getPreviewLoadDelay(): number;
  getPdfPreviewShouldFail(): boolean { return false; }
  abstract watchUploadStatus(): Observable<StreamStatus>;
  abstract listImageFolders(path: string): Observable<ImageFolderList>;
  abstract getImageSummary(folder: string): Observable<ImageSummaryData>;
  abstract listImageGifs(folder: string, page: number, pageSize: number): Observable<ImageGifList>;
  abstract runPreprocessing(folder: string): Observable<void>;
  abstract watchPreprocessingStatus(folder: string): Observable<StreamStatus>;
  abstract getGifUrl(path: string): string;

  abstract getValidationSummary(): Observable<ValidationSummary>;
  abstract notifyReplan(failedVariables: string[]): Observable<void>;
  abstract getPipelineStats(): Observable<PipelineStats>;

  // ── Strategy & Planning ───────────────────────────────────

  abstract getReviewData(decisionId: string): Observable<ReviewVariableData>;

  // Step 1 → Step 2: user changed decision
  // mappingFrom carries the sources the user confirmed. Sent for every decision that has a
  // Sources step — calculated_variable, mapped and partially_mapped — and omitted for
  // matched and not_mapped, which take no sources.
  // selectedTargetId regenerates against a specific target. It is what a target change uses
  // when the decision carries sources: `transforms/start` has no field for `mapping_from`, so
  // the confirmed source set would not reach the regeneration and the transformations would be
  // built against the sources still recorded on the plan.
  abstract startDecisionChange(decisionId: string, decision: string, mappingFrom?: string[], selectedTargetId?: string): Observable<{ ok: boolean }>;
  abstract watchDecisionChangeStatus(decisionId: string, decision: string): Observable<StreamStatus>;
  abstract getDecisionTargets(decisionId: string, decision: string): Observable<TargetReloadData>;

  // Step 2 → Step 3: user changed target
  // mappingFrom is the confirmed source set, sent for the three decisions that carry one.
  // The reloaded transformations are rebuilt for the CURRENT sources: omitting it makes the
  // backend fall back to whatever the review still has on file, which after a source edit is
  // the selection the user already replaced.
  abstract startTransformReload(decisionId: string, decision: string, targetId: string, mappingFrom?: string[]): Observable<{ ok: boolean }>;
  abstract watchTransformReloadStatus(decisionId: string, decision: string, targetId: string): Observable<StreamStatus>;
  abstract getTargetTransforms(decisionId: string, targetId: string): Observable<TransformReloadData>;

  // mappingFrom carries the confirmed sources; omitted for matched and not_mapped.
  abstract markVariableReviewed(
    decisionId: string,
    decision: string,
    targetId: string | null,
    selectedTransformation: TransformationOption | null,
    mappingFrom?: string[],
  ): Observable<void>;

  abstract splitVariable(decisionId: string): Observable<SplitVariableResult>;

  abstract suggestTransformation(
    decisionId: string,
    decision: string,
    targetId: string,
    selectedTransformation: TransformationOption | null,
    userMessage: string,
    history: TransformChatMessage[],
  ): Observable<TransformSuggestion>;

  /** Create a new custom transformation from a user-authored `code`: the backend applies +
   *  validates it and returns the full card (id, title, description, confidence, preview,
   *  notes, code), or errors if invalid. */
  abstract createTransform(
    decisionId: string,
    targetId: string,
    code: Record<string, unknown>[],
  ): Observable<TransformationOption>;

  /** Edit an existing transformation by `id`: re-apply/validate the code and return the
   *  updated card (with a fresh preview), or errors if invalid. */
  abstract editTransform(
    decisionId: string,
    transformationId: string,
    code: Record<string, unknown>[],
  ): Observable<TransformationOption>;

  // ── Pipeline status streams ───────────────────────────────

  abstract reconnectUploadStatus(): Observable<StreamStatus>;
  abstract watchProfilingStatus(): Observable<StreamStatus>;
  abstract reconnectProfilingStatus(): Observable<StreamStatus>;
  abstract watchPlanningStatus(): Observable<StreamStatus>;
  abstract reconnectPlanningStatus(): Observable<StreamStatus>;
  abstract watchExecutionStatus(): Observable<StreamStatus>;
  abstract reconnectExecutionStatus(): Observable<StreamStatus>;
  abstract startExecution(): Observable<{ ok: boolean; session_id: string }>;
  /** The execution breakdown behind the Execution Module's chart: the plan's split by
   *  decision, and how much of each made it through the run. */
  abstract getExecutionSummary(): Observable<ExecutionSummary>;

  /** The variables the run did not manage to write, every decision at once. The summary
   *  already says how many; this says which ones. There is no `decision` filter on purpose:
   *  the list is bounded by the number of failures, so one call backs a screen that moves
   *  between decisions without going back to the server. Independent of how the screen
   *  chooses to show them. */
  abstract getExecutionFailures(): Observable<ExecutionFailures>;

  abstract getExecutionPreview(query: ExecutionPreviewQuery): Observable<ExecutionPreviewData>;

  /** Every distinct value of one column, uncapped and faceted the same way the preview
   *  facets it. Asked for only when the preview said that column's list was cut off — the
   *  funnel cannot present a truncated list as if it were the whole set. */
  abstract getColumnValues(q: ColumnValuesQuery): Observable<ColumnValuesData>;
  abstract getPlanVariables(page: number, pageSize: number, opts?: PlanQueryOpts): Observable<PagedResult<AuditVariable>>;
  abstract searchPlanVariables(q: string, page: number, pageSize: number, opts?: PlanQueryOpts): Observable<PagedResult<AuditVariable>>;
  abstract getValidationResults(page: number, pageSize: number, sortBy?: string): Observable<PagedResult<ValidationRow>>;
  abstract searchValidationResults(q: string, page: number, pageSize: number, sortBy?: string): Observable<PagedResult<ValidationRow>>;
  abstract searchProfileVariables(dataset: string, q: string, page: number, pageSize: number, opts?: ProfileQueryOpts): Observable<PagedResult<ProfileVariableRow>>;
  abstract getProfileVariables(dataset: string, page: number, pageSize: number, opts?: ProfileQueryOpts): Observable<PagedResult<ProfileVariableRow>>;

  // ── Data Profiling: variable edit ──
  abstract getProfileVariableEditSuggestions(dataset: string, variableName: string): Observable<ProfileVariableEditDefaults>;
  abstract updateProfileVariable(dataset: string, variableName: string, patch: ProfileVariableEditPatch): Observable<void>;
  abstract deleteProfileVariable(dataset: string, variableName: string): Observable<void>;

  // ── Session history ───────────────────────────────────────

  abstract getSessions(): Observable<Session[]>;
  abstract renameSession(sessionId: string, name: string): Observable<{ ok: boolean }>;
  abstract deleteSession(sessionId: string): Observable<void>;
  abstract finalizeSession(): Observable<{ ok: boolean }>;

  /** Stop the run's in-flight work, keeping the session. Called from the step-change
   *  loading modal's "Cancel run", after the user confirms — the only place a user can
   *  stop a stage that's already executing. */
  abstract cancelSession(): Observable<{ ok: boolean }>;
}

@Injectable({ providedIn: 'root' })
export class HelpBubbleService {
  readonly hasContent = signal(false);
  readonly isOpen = signal(false);

  register(): void { this.hasContent.set(true); }
  unregister(): void { this.hasContent.set(false); this.isOpen.set(false); }
  toggle(): void { this.isOpen.update(v => !v); }
  close(): void { this.isOpen.set(false); }
}
